export interface FruitSnapshot {
  t: number;
  nx: number;
  ny: number;
  rotationRad: number;
}

const MAX_SAMPLES = 4;
const MAX_AGE_MS = 500;

function shortestAngleDelta(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/**
 * Кольцевой буфер позиций фруктов по стабильному id от нативы.
 * Рендерим на `now - renderDelayMs` → всегда есть 2 сэмпла для интерполяции
 * между последней и предпоследней нативной выборкой.
 */
export class FruitPositionBuffer {
  private readonly byId = new Map<string | number, FruitSnapshot[]>();
  private readonly liveIdsBuf = new Set<string | number>();

  constructor(private readonly renderDelayMs = 80) {}

  push(id: string | number, snap: FruitSnapshot): void {
    let arr = this.byId.get(id);
    if (!arr) {
      arr = [];
      this.byId.set(id, arr);
    }
    arr.push(snap);
    while (arr.length > MAX_SAMPLES) arr.shift();
  }

  sampleAt(id: string | number, nowMs: number): FruitSnapshot | null {
    const arr = this.byId.get(id);
    if (!arr || arr.length === 0) return null;
    const targetT = nowMs - this.renderDelayMs;

    if (arr.length === 1) return arr[0]!;

    for (let i = 0; i < arr.length - 1; i++) {
      const a = arr[i]!;
      const b = arr[i + 1]!;
      if (a.t <= targetT && targetT <= b.t) {
        const span = b.t - a.t;
        const k = span > 1e-3 ? (targetT - a.t) / span : 0;
        return {
          t: targetT,
          nx: a.nx + (b.nx - a.nx) * k,
          ny: a.ny + (b.ny - a.ny) * k,
          rotationRad: a.rotationRad + shortestAngleDelta(a.rotationRad, b.rotationRad) * k,
        };
      }
    }

    const last = arr[arr.length - 1]!;
    if (targetT < arr[0]!.t) return arr[0]!;
    return last;
  }

  forget(id: string | number): void {
    this.byId.delete(id);
  }

  retainOnly(liveIds: Iterable<string | number>): void {
    const live = this.liveIdsBuf;
    live.clear();
    for (const id of liveIds) live.add(id);
    this.byId.forEach((_v, id) => {
      if (!live.has(id)) this.byId.delete(id);
    });
  }

  cleanup(nowMs: number): void {
    this.byId.forEach((arr, id) => {
      const last = arr[arr.length - 1];
      if (!last || nowMs - last.t > MAX_AGE_MS) {
        this.byId.delete(id);
      }
    });
  }

  clear(): void {
    this.byId.clear();
  }

  size(): number {
    return this.byId.size;
  }
}
